import {
    Autocomplete, Box, Card, CardHeader, Checkbox, Divider, Grid, IconButton, List, ListItem,
    ListItemButton, ListItemIcon, ListItemText, Stack, TextField, Typography
} from '@mui/material'
import CachedIcon from '@mui/icons-material/Cached'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { EntityWithIdAndDescriptionFields } from 'src/types'
import { HttpSectorSistemaGrupo } from './api'
import { HttpSystems } from 'src/http/workflow/httpSystems'
import { SistemaFields } from 'src/pages/messages/types'
import { EntityWithIdAndDescriptionFields as EFields } from 'src/types'
import { SectorDisponibleDTO, SectorDisponibleFields, GrupoDisponibleDTO, GrupoDisponibleFields } from './types'
import { Controller, useForm } from 'react-hook-form'
import RUTAS from 'src/pages/admin/router/routes'

type FilterForm = { codTipo: any; codPais: any }

function TabConfPorSistema() {
    // Trae, en una sola llamada, los tipos de sistema, paises (SistemaPrincipal) y sistemas activos
    const { data: selectoresData } = useQuery({
        queryKey: ['sistema-selectores'],
        queryFn: () => HttpSystems.getSelectores(),
        staleTime: Infinity
    })
    const tipos = selectoresData?.tipos ?? []
    const paises = selectoresData?.paises ?? []
    const sistemas = selectoresData?.sistemas ?? []

    const [sectores, setSectores] = useState<SectorDisponibleDTO[]>([])
    const [checkedSectores, setCheckedSectores] = useState<number[]>([])
    const [selectedSector, setSelectedSector] = useState<SectorDisponibleDTO | null>(null)
    const [grupos, setGrupos] = useState<GrupoDisponibleDTO[]>([])
    const [checkedGrupos, setCheckedGrupos] = useState<number[]>([])

    const navigate = useNavigate()
    const { control, watch, setValue } = useForm<FilterForm>({ defaultValues: { codTipo: null, codPais: null } })
    const selectedTipo = watch('codTipo')
    const selectedPais = watch('codPais')
    const codTipo = selectedTipo?.id ?? selectedTipo
    const codPais = selectedPais?.id ?? selectedPais

    const tipoOptions = useMemo(
        () => tipos.map(t => ({ id: t.id, descripcion: t[EntityWithIdAndDescriptionFields.Description] })),
        [tipos]
    )

    // País por cada tipo de sistema (id de sistemaTipo -> set de ids de país que lo tienen disponible)
    const paisesPorTipo = useMemo(() => {
        const map = new Map<number, Set<number>>()
        sistemas.forEach(s => {
            const tipoId = s[SistemaFields.SistemaTipo]
            const paisId = s[SistemaFields.SistemaPrincipal]
            if (!map.has(tipoId)) map.set(tipoId, new Set())
            map.get(tipoId)!.add(paisId)
        })

        return map
    }, [sistemas])

    // Paises del tipo seleccionado, filtrados en memoria a partir del listado completo ya cargado
    const paisOptions = useMemo(() => {
        if (!codTipo) return []
        const idsPaises = paisesPorTipo.get(codTipo)
        if (!idsPaises) return []

        return paises
            .filter(p => idsPaises.has(p.id))
            .map(p => ({ id: p.id, descripcion: p[EntityWithIdAndDescriptionFields.Description] }))
    }, [paises, paisesPorTipo, codTipo])

    // Resuelve el Sistema (circuito) real a partir del Tipo y el País seleccionados
    const codSistema = useMemo(() => {
        if (!codTipo || !codPais) return undefined

        return sistemas.find(
            s => s[SistemaFields.SistemaTipo] === codTipo && s[SistemaFields.SistemaPrincipal] === codPais
        )?.id
    }, [sistemas, codTipo, codPais])

    // Limpiar país seleccionado cuando cambia el tipo
    useEffect(() => {
        setValue('codPais', null)
    }, [selectedTipo])

    // Cargar sectores cuando cambia el sistema resuelto
    useEffect(() => {
        if (!codSistema) { setSectores([]); setSelectedSector(null); setGrupos([]);

return }
        fetchSectores()
    }, [codSistema])

    const fetchSectores = () => {
        if (!codSistema) return
        HttpSectorSistemaGrupo.getSectoresPorSistema(codSistema, codPais).then(data => {
            setSectores(data)
            setCheckedSectores(data.filter(s => s[SectorDisponibleFields.Activo] === 1).map(s => s[SectorDisponibleFields.CodSector]))
            setSelectedSector(null); setGrupos([]); setCheckedGrupos([])
        })
    }

    // Cargar grupos cuando se selecciona un sector
    useEffect(() => {
        if (!selectedSector || !codSistema) { setGrupos([]); setCheckedGrupos([]); 

return }
        HttpSectorSistemaGrupo.getGruposPorSistemaYSector(codSistema, selectedSector[SectorDisponibleFields.CodSector])
            .then(data => {
                setGrupos(data)
                setCheckedGrupos(data.filter(g => g[GrupoDisponibleFields.PerteneceASector] === 1).map(g => g[GrupoDisponibleFields.IdGrupo]))
            })
    }, [selectedSector])

    const handleToggleSector = (cod: number) => {
        setCheckedSectores(prev => prev.includes(cod) ? prev.filter(id => id !== cod) : [...prev, cod])
    }

    const handleSaveSectores = () => {
        if (!codSistema) return Promise.resolve()
        const promise = HttpSectorSistemaGrupo.updateSectoresPorSistema(codSistema, codPais, checkedSectores)
            .then(() => fetchSectores())
        toast.promise(promise, { success: 'Sectores actualizados', error: 'Error al guardar', loading: 'Guardando' })
        
return promise
    }

    const handleToggleGrupo = (idGrupo: number) => {
        setCheckedGrupos(prev => prev.includes(idGrupo) ? prev.filter(id => id !== idGrupo) : [...prev, idGrupo])
    }

    const handleSaveGrupos = () => {
        if (!selectedSector || !codSistema) return Promise.resolve()
        const codSector = selectedSector[SectorDisponibleFields.CodSector]
        const promise = HttpSectorSistemaGrupo.updateGrupos(codSector, codSistema, checkedGrupos).then(() => {
            // Refrescar sectores (se actualizan por trigger)
            fetchSectores()
            if (codSistema) {
                HttpSectorSistemaGrupo.getGruposPorSistemaYSector(codSistema, codSector).then(data => {
                    setGrupos(data)
                    setCheckedGrupos(data.filter(g => g[GrupoDisponibleFields.PerteneceASector] === 1).map(g => g[GrupoDisponibleFields.IdGrupo]))
                })
            }
        })
        toast.promise(promise, { success: 'Grupos actualizados', error: 'Error al guardar', loading: 'Guardando' })
        
return promise
    }

    return (
        <Box>
            <Box sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', px: 2, py: 1.5 }}>
                    <Button variant='text' color='primary' size='small' startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/${RUTAS.PANEL}`)}>
                        Volver
                    </Button>
                    <Divider orientation='vertical' flexItem />
                    <Typography variant='subtitle2' fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                        Conf. por Sistema
                    </Typography>
                    <Box sx={{ minWidth: 150, flex: 1 }}>
                        <Controller control={control} name='codTipo' render={({ field }) => (
                            <Autocomplete options={tipoOptions}
                                value={field.value} onChange={(_, v) => field.onChange(v)}
                                getOptionLabel={(o: any) => o.descripcion ?? ''} isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                                size='small' renderInput={params => <TextField {...params} label='Circuito *' />}
                            />
                        )} />
                    </Box>
                    <Box sx={{ minWidth: 240, flex: 2 }}>
                        <Controller control={control} name='codPais' render={({ field }) => (
                            <Autocomplete disabled={!selectedTipo} options={paisOptions}
                                value={field.value} onChange={(_, v) => field.onChange(v)}
                                getOptionLabel={(o: any) => o.descripcion ?? ''} isOptionEqualToValue={(o: any, v: any) => o.id === v?.id}
                                size='small' renderInput={params => <TextField {...params} label='País *' />}
                            />
                        )} />
                    </Box>
                    {codSistema && <IconButton size='small' onClick={fetchSectores}><CachedIcon /></IconButton>}
                    {codSistema && sectores.length > 0 && (
                        <Button variant='outlined' color='primary' size='small' promise={handleSaveSectores}>Guardar sectores</Button>
                    )}
                    {selectedSector && grupos.length > 0 && (
                        <Button variant='contained' color='primary' size='small' promise={handleSaveGrupos}>Guardar grupos</Button>
                    )}
                </Box>
            </Box>

            {codSistema ? (
                <Box sx={{ mx: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                            <Card variant='outlined' sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                                <CardHeader title='Sectores Comerciales' titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} sx={{ pb: 0 }}
                                />
                                <Box sx={{ overflow: 'auto', flex: 1 }}>
                                    <List dense>
                                        {sectores.map(sector => {
                                            const cod = sector[SectorDisponibleFields.CodSector]
                                            
return (
                                                <ListItem key={cod} disablePadding>
                                                    <ListItemButton selected={selectedSector?.[SectorDisponibleFields.CodSector] === cod} onClick={() => setSelectedSector(sector)}>
                                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                                            <Checkbox edge='start' checked={checkedSectores.includes(cod)}
                                                                onClick={(e) => { e.stopPropagation(); handleToggleSector(cod) }} size='small' />
                                                        </ListItemIcon>
                                                        <ListItemText primary={sector[SectorDisponibleFields.NombreSector]} />
                                                    </ListItemButton>
                                                </ListItem>
                                            )
                                        })}
                                        {sectores.length === 0 && <ListItem><ListItemText primary='No hay sectores disponibles' sx={{ color: 'text.secondary', textAlign: 'center' }} /></ListItem>}
                                    </List>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Card variant='outlined' sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                                <CardHeader title={selectedSector ? `Grupos de ${selectedSector[SectorDisponibleFields.NombreSector]}` : 'Grupos'}
                                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} sx={{ pb: 0 }}
                                />
                                <Box sx={{ overflow: 'auto', flex: 1 }}>
                                    {selectedSector ? (
                                        <List dense>
                                            {grupos.map(grupo => (
                                                <ListItem key={grupo[GrupoDisponibleFields.IdGrupo]} disablePadding>
                                                    <ListItemButton onClick={() => handleToggleGrupo(grupo[GrupoDisponibleFields.IdGrupo])}>
                                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                                            <Checkbox edge='start' checked={checkedGrupos.includes(grupo[GrupoDisponibleFields.IdGrupo])} tabIndex={-1} disableRipple size='small' />
                                                        </ListItemIcon>
                                                        <ListItemText primary={grupo[GrupoDisponibleFields.NombreGrupo]} />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                            {grupos.length === 0 && <ListItem><ListItemText primary='No hay grupos para este sector' sx={{ color: 'text.secondary', textAlign: 'center' }} /></ListItem>}
                                        </List>
                                    ) : (
                                        <Box sx={{ p: 4, textAlign: 'center' }}><Typography color='text.secondary'>Seleccione un sector para ver sus grupos</Typography></Box>
                                    )}
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}><Typography color='text.secondary'>Seleccione un país y sistema para configurar</Typography></Box>
            )}
        </Box>
    )
}

export default TabConfPorSistema
